// src/pages/ResultsPage.js — Light Theme Redesign
import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, useInView } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { db } from '../firebase';
import { doc, collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { Link } from 'react-router-dom';

/* ============================================================
   ANIMATED COUNT-UP HOOK
============================================================ */
function useCountUp(target, duration = 1600) {
  const [value, setValue] = useState(0);
  const prev = useRef(0);

  useEffect(() => {
    if (target == null) return;
    const start = prev.current;
    const end = Number(target);
    const startTime = performance.now();
    const tick = (now) => {
      const elapsed = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - elapsed, 3);
      setValue(start + (end - start) * eased);
      if (elapsed < 1) requestAnimationFrame(tick);
      else { setValue(end); prev.current = end; }
    };
    requestAnimationFrame(tick);
  }, [target, duration]);

  return value;
}

/* ============================================================
   KPI CARD COMPONENT
============================================================ */
const KpiCard = ({ value, label, sublabel, prefix = '', suffix = '', color, isFloat, delay = 0, icon, highlight }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const [triggered, setTriggered] = useState(false);

  useEffect(() => { if (inView) setTriggered(true); }, [inView]);

  const animated = useCountUp(triggered ? value : 0, 1600);
  const display = isFloat ? animated.toFixed(1) : Math.round(animated).toLocaleString();

  return (
    <KpiCardWrap
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.5, ease: 'easeOut' }}
      highlight={highlight}
    >
      {highlight && <KpiCardAccent />}
      {icon && <KpiIcon>{icon}</KpiIcon>}
      <KpiValue color={color}>{prefix}{display}{suffix}</KpiValue>
      <KpiLabel>{label}</KpiLabel>
      {sublabel && <KpiSublabel>{sublabel}</KpiSublabel>}
    </KpiCardWrap>
  );
};

/* ============================================================
   HELPERS
============================================================ */
function fmtPrice(val) {
  if (val == null || val === '') return '—';
  const n = Number(val);
  if (isNaN(n)) return '—';
  if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (n >= 1) return n.toFixed(4);
  if (n >= 0.0001) return n.toFixed(6);
  return n.toPrecision(4);
}

function calcGain(signal) {
  const entry = parseFloat(signal.entryPrice || signal.averageEntryPrice);
  const close = parseFloat(signal.closePrice);
  if (!entry || !close || isNaN(entry) || isNaN(close)) return null;
  const isShort = (signal.signalType || '').toUpperCase() === 'SHORT';
  return isShort ? ((entry - close) / entry) * 100 : ((close - entry) / entry) * 100;
}

function fmtTs(ts) {
  if (!ts) return '—';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function getStatusMeta(status = '') {
  const s = status.toUpperCase();
  if (s.includes('BOTH')) return { label: 'BOTH TP HIT', color: '#10b981', bg: 'rgba(16,185,129,0.15)' };
  if (s.includes('TP2')) return { label: 'TP2 HIT', color: '#10b981', bg: 'rgba(16,185,129,0.12)' };
  if (s.includes('TP1')) return { label: 'TP1 HIT', color: '#34d399', bg: 'rgba(52,211,153,0.12)' };
  if (s.includes('SL') || s.includes('STOP')) return { label: 'SL HIT', color: '#ef4444', bg: 'rgba(239,68,68,0.10)' };
  if (s.includes('INVALID')) return { label: 'INVALID', color: '#9ca3af', bg: 'rgba(156,163,175,0.1)' };
  if (s.includes('PENDING')) return { label: 'PENDING', color: '#F7941D', bg: 'rgba(247,148,29,0.12)' };
  if (s.includes('ACTIVE')) return { label: 'ACTIVE', color: '#29ABE2', bg: 'rgba(41,171,226,0.12)' };
  return { label: status || 'CLOSED', color: '#6b7280', bg: 'rgba(107,114,128,0.1)' };
}

/* ============================================================
   MAIN PAGE COMPONENT
============================================================ */
const ResultsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closedSignals, setClosedSignals] = useState([]);
  const [pendingSignals, setPendingSignals] = useState([]);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'statistics', 'liveResults'),
      (snap) => {
        if (snap.exists()) setStats(snap.data());
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, []);

  // Last 20 closed signals — Firestore excludes docs without closedAt when using orderBy
  useEffect(() => {
    const q = query(
      collection(db, 'signals'),
      orderBy('closedAt', 'desc'),
      limit(20)
    );
    const unsub = onSnapshot(q, (snap) => {
      setClosedSignals(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, () => {});
    return () => unsub();
  }, []);

  // Up to 3 pending signals
  useEffect(() => {
    const q = query(
      collection(db, 'signals'),
      where('status', '==', 'PENDING'),
      orderBy('createdAt', 'desc'),
      limit(3)
    );
    const unsub = onSnapshot(q, (snap) => {
      setPendingSignals(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, () => {});
    return () => unsub();
  }, []);

  const wins = stats ? (stats.tp1Hits || 0) + (stats.tp2Hits || 0) : 0;
  const totalResolved = stats ? wins + (stats.slHits || 0) : 0;
  const winRate = totalResolved > 0 ? Math.round((wins / totalResolved) * 100) : 0;
  const lossRate = 100 - winRate;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    name: 'Trend Genie — Free AI Crypto Signals App',
    operatingSystem: 'Android',
    applicationCategory: 'FinanceApplication',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.8', ratingCount: '1500' },
    url: 'https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie',
    description: 'AI-powered crypto signals for Android. Real-time alerts with entry, TP and SL levels. Free to download.',
  };

  return (
    <PageWrapper>
      <Helmet>
        <title>Live AI Crypto Signal Results — Trend Genie Real Performance Tracker</title>
        <meta name="description" content="See Trend Genie's live AI crypto signal results. Real performance data — no backtests. Track portfolio growth, TP hits, win rate and more. Download the free crypto signals app for Android." />
        <meta name="keywords" content="AI crypto signals results, crypto signal performance, best crypto signals app, free crypto signals android, trend genie results, AI trading signals track record" />
        <link rel="canonical" href="https://www.trendgenie.io/live-results" />
        <meta property="og:title" content="Live AI Crypto Signal Results — Trend Genie" />
        <meta property="og:description" content="Real performance data from Trend Genie's AI crypto signal engine. No backtests — live tracked results updated automatically." />
        <meta property="og:image" content="https://www.trendgenie.io/social-share-image.png" />
        <meta property="og:url" content="https://www.trendgenie.io/live-results" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Live AI Crypto Signal Results — Trend Genie" />
        <meta name="twitter:description" content="Real signal performance. Real win rates. Free crypto signals app for Android." />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      {/* ── HERO ── */}
      <HeroSection>
        <HeroBg />
        <HeroBg2 />
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <LiveBadge><LiveDot />Live Performance Data</LiveBadge>
          <HeroTitle>
            Real AI Signals.<br />
            <GradientSpan>Real Results.</GradientSpan>
          </HeroTitle>
          <HeroSub>
            Every signal Trend Genie fires is tracked from entry to exit — automatically.
            No cherry-picked screenshots. No backtests. Just live, verified performance.
          </HeroSub>
          <HeroCTARow>
            <DownloadBtn
              href="https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie"
              target="_blank" rel="noopener noreferrer"
            >
              Download Free on Android
            </DownloadBtn>
            <SecondaryBtn href="#kpis">View Live KPIs</SecondaryBtn>
          </HeroCTARow>
          <HeroNote>Free to download &nbsp;·&nbsp; No credit card required &nbsp;·&nbsp; Android only</HeroNote>
        </motion.div>
      </HeroSection>

      {/* ── LOADING STATE ── */}
      {loading && (
        <LoadingSection>
          <LoadingDot /><LoadingDot style={{ animationDelay: '0.2s' }} /><LoadingDot style={{ animationDelay: '0.4s' }} />
          <LoadingText>Loading live data…</LoadingText>
        </LoadingSection>
      )}

      {!loading && stats && (
        <>
          {/* ── HERO KPI STRIP ── */}
          <HeroKpiStrip id="kpis">
            <HeroKpi>
              <HeroKpiVal green>+{Number(stats.growth).toFixed(1)}%</HeroKpiVal>
              <HeroKpiLabel>Portfolio Growth</HeroKpiLabel>
            </HeroKpi>
            <HeroKpiDivider />
            <HeroKpi>
              <HeroKpiVal>{stats.totalSignals?.toLocaleString()}</HeroKpiVal>
              <HeroKpiLabel>Total Signals</HeroKpiLabel>
            </HeroKpi>
            <HeroKpiDivider />
            <HeroKpi>
              <HeroKpiVal green>{winRate}%</HeroKpiVal>
              <HeroKpiLabel>Win Rate</HeroKpiLabel>
            </HeroKpi>
            <HeroKpiDivider />
            <HeroKpi>
              <HeroKpiVal blue>{stats.active}</HeroKpiVal>
              <HeroKpiLabel>Active Now</HeroKpiLabel>
            </HeroKpi>
          </HeroKpiStrip>

          {/* ── KPI GRID ── */}
          <ContentSection>
            <SectionTag>Key Performance Indicators</SectionTag>
            <SectionTitle>Everything Tracked, Nothing Hidden</SectionTitle>
            <SectionSub>
              These numbers update in real time from our system. Every signal logged, every outcome recorded.
            </SectionSub>

            <KpiGrid>
              <KpiCard value={stats.growth} prefix="+" suffix="%" label="Portfolio Growth" sublabel="$100/trade · 10x leverage" color="#10b981" isFloat delay={0} highlight />
              <KpiCard value={stats.totalSignals} label="Total Signals Fired" sublabel="Since launch" color="var(--primary-color)" delay={0.07} />
              <KpiCard value={stats.tp1Hits} label="TP1 Targets Hit" sublabel="First take-profit reached" color="#10b981" delay={0.14} />
              <KpiCard value={stats.tp2Hits} label="TP2 Targets Hit" sublabel="Full target reached" color="#10b981" delay={0.21} />
              <KpiCard value={stats.slHits} label="Stop Loss Hits" sublabel="Risk-managed exits" color="#ef4444" delay={0.28} />
              <KpiCard value={winRate} suffix="%" label="Win Rate" sublabel="TP1 + TP2 vs total resolved" color="#F7941D" delay={0.35} highlight />
              <KpiCard value={stats.active} label="Active Signals" sublabel="Currently in play" color="var(--primary-color)" delay={0.42} />
              <KpiCard value={stats.pending} label="Pending Signals" sublabel="Awaiting market confirmation" color="#F7941D" delay={0.49} />
              <KpiCard value={stats.invalidated} label="Invalidated" sublabel="Hit SL before entry" color="var(--text-muted)" delay={0.56} />
            </KpiGrid>
          </ContentSection>

          {/* ── WIN/LOSS BREAKDOWN ── */}
          <BreakdownSection>
            <SectionTag>Win / Loss Breakdown</SectionTag>
            <SectionTitle>Wins vs Losses — Transparently</SectionTitle>
            <SectionSub>We show you the wins AND the losses. That's what a real track record looks like.</SectionSub>

            <BreakdownCard>
              <BreakdownRow>
                <BreakdownSide type="win">
                  <BreakdownIcon type="win">↑</BreakdownIcon>
                  <BreakdownBigNum>{(stats.tp1Hits || 0) + (stats.tp2Hits || 0)}</BreakdownBigNum>
                  <BreakdownLabel>Total Wins</BreakdownLabel>
                  <BreakdownSubs>
                    <BreakdownSub>TP1: <strong>{stats.tp1Hits || 0}</strong></BreakdownSub>
                    <BreakdownSub>TP2: <strong>{stats.tp2Hits || 0}</strong></BreakdownSub>
                  </BreakdownSubs>
                </BreakdownSide>

                <BreakdownCenter>
                  <WinRateCircle>
                    <WinRateCircleVal>{winRate}%</WinRateCircleVal>
                    <WinRateCircleLabel>Win Rate</WinRateCircleLabel>
                  </WinRateCircle>
                </BreakdownCenter>

                <BreakdownSide type="loss">
                  <BreakdownIcon type="loss">↓</BreakdownIcon>
                  <BreakdownBigNum>{stats.slHits || 0}</BreakdownBigNum>
                  <BreakdownLabel>Stop Losses</BreakdownLabel>
                  <BreakdownSubs>
                    <BreakdownSub>Loss Rate: <strong>{lossRate}%</strong></BreakdownSub>
                  </BreakdownSubs>
                </BreakdownSide>
              </BreakdownRow>

              {/* Stacked bar */}
              <BarSection>
                <BarLabel>
                  <span style={{ color: '#10b981' }}>{winRate}% wins</span>
                  <span style={{ color: '#ef4444' }}>{lossRate}% losses</span>
                </BarLabel>
                <StackedBarTrack>
                  <motion.div
                    style={{ height: '100%', background: 'linear-gradient(90deg, #10b981, #34d399)', borderRadius: '999px 0 0 999px' }}
                    initial={{ width: 0 }}
                    whileInView={{ width: `${winRate}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.4, ease: 'easeOut', delay: 0.3 }}
                  />
                  <motion.div
                    style={{ height: '100%', background: 'linear-gradient(90deg, #fca5a5, #ef4444)', borderRadius: '0 999px 999px 0', marginLeft: 'auto' }}
                    initial={{ width: 0 }}
                    whileInView={{ width: `${lossRate}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.4, ease: 'easeOut', delay: 0.3 }}
                  />
                </StackedBarTrack>
              </BarSection>
            </BreakdownCard>
          </BreakdownSection>

          {/* ── HOW IT WORKS ── */}
          <HowItWorksSection>
            <SectionTag>How It Works</SectionTag>
            <SectionTitle>From AI Scan to Signal in Seconds</SectionTitle>
            <SectionSub>Our engine runs 24/7 scanning price action, volume and market structure to surface high-probability setups.</SectionSub>

            <StepsRow>
              {[
                { num: '1', title: 'AI Scans the Market', desc: 'Millions of data points analyzed 24/7 — price action, volume, momentum and structure across dozens of pairs.' },
                { num: '2', title: 'Signal Generated', desc: 'A clear trade setup fires with exact entry price, TP1, TP2 and SL levels. No guessing required.' },
                { num: '3', title: 'You Get Notified', desc: 'Instant push notification on your Android device. Open the app, review the signal and execute.' },
                { num: '4', title: 'Outcome Recorded', desc: 'Every signal result — win, loss or invalidation — is tracked and added to the live performance dashboard.' },
              ].map((step, i) => (
                <StepCard
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                >
                  <StepNum>{step.num}</StepNum>
                  <StepTitle>{step.title}</StepTitle>
                  <StepDesc>{step.desc}</StepDesc>
                </StepCard>
              ))}
            </StepsRow>
          </HowItWorksSection>

          {/* ── COMPARISON ── */}
          <ComparisonSection>
            <SectionTag>Performance Metrics</SectionTag>
            <SectionTitle>Trend Genie vs Manual Trading</SectionTitle>
            <SectionSub>Data-driven decisions beat emotional trading every time.</SectionSub>

            <ComparisonGrid>
              {[
                { label: 'AI Reaction Speed', ai: 100, human: 18, aiText: 'Instant — milliseconds', humanText: 'Minutes to hours' },
                { label: 'Emotion-Free Execution', ai: 100, human: 30, aiText: 'Zero emotion, pure data', humanText: 'Fear & greed interfere' },
                { label: 'Market Coverage 24/7', ai: 95, human: 25, aiText: 'Never sleeps', humanText: 'Limited hours' },
              ].map((item, i) => (
                <CompItem
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12 }}
                >
                  <CompLabel>{item.label}</CompLabel>
                  <CompBarRow>
                    <CompBarTag ai>AI</CompBarTag>
                    <CompBarTrack>
                      <motion.div
                        style={{ height: '100%', background: 'linear-gradient(90deg, #29ABE2, #1A5276)', borderRadius: '999px' }}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${item.ai}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2, ease: 'easeOut', delay: 0.2 + i * 0.1 }}
                      />
                    </CompBarTrack>
                    <CompBarPct ai>{item.ai}%</CompBarPct>
                  </CompBarRow>
                  <CompBarRow>
                    <CompBarTag>Human</CompBarTag>
                    <CompBarTrack>
                      <motion.div
                        style={{ height: '100%', background: 'linear-gradient(90deg, #d1d5db, #9ca3af)', borderRadius: '999px' }}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${item.human}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2, ease: 'easeOut', delay: 0.3 + i * 0.1 }}
                      />
                    </CompBarTrack>
                    <CompBarPct>{item.human}%</CompBarPct>
                  </CompBarRow>
                  <CompNote ai>{item.aiText}</CompNote>
                  <CompNote>{item.humanText}</CompNote>
                </CompItem>
              ))}
            </ComparisonGrid>
          </ComparisonSection>

          {/* ── PENDING SIGNALS ── */}
          {pendingSignals.length > 0 && (
            <PendingSection>
              <SectionTag>Live Now</SectionTag>
              <SectionTitle>Pending Signals — Waiting to Play Out</SectionTitle>
              <SectionSub>These signals have been fired and are currently active in the market.</SectionSub>

              <PendingGrid>
                {pendingSignals.map((sig, i) => (
                  <motion.div
                    key={sig.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1, duration: 0.5 }}
                  >
                    <PendingCard>
                      <PendingCardTop>
                        <PendingCoin>
                          <PendingSymbol>{(sig.symbol || sig.coinId || '—').toUpperCase()}</PendingSymbol>
                          <PendingExchange>{sig.exchange || ''}</PendingExchange>
                        </PendingCoin>
                        <DirectionBadge short={(sig.signalType || '').toUpperCase() === 'SHORT'}>
                          {(sig.signalType || 'LONG').toUpperCase()}
                        </DirectionBadge>
                      </PendingCardTop>

                      <PendingPrices>
                        <PendingPriceRow>
                          <PendingPriceLabel>Entry</PendingPriceLabel>
                          <PendingPriceVal entry>{fmtPrice(sig.entryPrice || sig.averageEntryPrice)}</PendingPriceVal>
                        </PendingPriceRow>
                        <PendingPriceRow>
                          <PendingPriceLabel>TP1</PendingPriceLabel>
                          <PendingPriceVal tp>{fmtPrice(sig.takeProfit)}</PendingPriceVal>
                        </PendingPriceRow>
                        <PendingPriceRow>
                          <PendingPriceLabel>TP2</PendingPriceLabel>
                          <PendingPriceVal tp>{fmtPrice(sig.takeProfit2)}</PendingPriceVal>
                        </PendingPriceRow>
                        <PendingPriceRow>
                          <PendingPriceLabel>Stop Loss</PendingPriceLabel>
                          <PendingPriceVal sl>{fmtPrice(sig.stopLoss)}</PendingPriceVal>
                        </PendingPriceRow>
                      </PendingPrices>

                      <PendingFooter>
                        <PendingTime>Generated: {fmtTs(sig.createdAt)}</PendingTime>
                        <PendingStatusBadge color="#F7941D" bg="rgba(247,148,29,0.12)">PENDING</PendingStatusBadge>
                      </PendingFooter>
                    </PendingCard>
                  </motion.div>
                ))}
              </PendingGrid>
            </PendingSection>
          )}

          {/* ── SIGNAL HISTORY ── */}
          {closedSignals.length > 0 && (
            <SignalHistorySection>
              <SectionTag>Signal History</SectionTag>
              <SectionTitle>Last 20 Closed Signals</SectionTitle>
              <SectionSub>Every signal tracked from entry to exit. No cherry-picking, no hiding losses.</SectionSub>

              <TableWrapper>
                <SignalTable>
                  <thead>
                    <SignalTHead>
                      <th>Coin</th>
                      <th>Direction</th>
                      <th>Entry Price</th>
                      <th>TP1</th>
                      <th>TP2</th>
                      <th>Close Price</th>
                      <th>Gain / Loss</th>
                      <th>Status</th>
                      <th>Closed</th>
                    </SignalTHead>
                  </thead>
                  <tbody>
                    {closedSignals.map((sig, i) => {
                      const gain = calcGain(sig);
                      const statusMeta = getStatusMeta(sig.status || '');
                      const isWin = gain !== null && gain > 0;
                      const isLoss = gain !== null && gain < 0;
                      return (
                        <SignalTRow
                          key={sig.id}
                          as={motion.tr}
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: Math.min(i * 0.03, 0.5) }}
                        >
                          <SignalTd>
                            <CoinCell>
                              <CoinSymbol>{(sig.symbol || sig.coinId || '—').toUpperCase()}</CoinSymbol>
                              <CoinExchange>{sig.exchange || ''}</CoinExchange>
                            </CoinCell>
                          </SignalTd>
                          <SignalTd>
                            <DirectionBadge short={(sig.signalType || '').toUpperCase() === 'SHORT'}>
                              {(sig.signalType || 'LONG').toUpperCase()}
                            </DirectionBadge>
                          </SignalTd>
                          <SignalTd mono>{fmtPrice(sig.entryPrice || sig.averageEntryPrice)}</SignalTd>
                          <SignalTd mono green>{fmtPrice(sig.takeProfit)}</SignalTd>
                          <SignalTd mono green>{fmtPrice(sig.takeProfit2)}</SignalTd>
                          <SignalTd mono>{fmtPrice(sig.closePrice)}</SignalTd>
                          <SignalTd>
                            {gain !== null ? (
                              <GainBadge win={isWin} loss={isLoss}>
                                {isWin ? '+' : ''}{gain.toFixed(2)}%
                              </GainBadge>
                            ) : '—'}
                          </SignalTd>
                          <SignalTd>
                            <StatusChip color={statusMeta.color} bg={statusMeta.bg}>
                              {statusMeta.label}
                            </StatusChip>
                          </SignalTd>
                          <SignalTd muted>{fmtTs(sig.closedAt)}</SignalTd>
                        </SignalTRow>
                      );
                    })}
                  </tbody>
                </SignalTable>
              </TableWrapper>
            </SignalHistorySection>
          )}
        </>
      )}

      {/* ── FINAL CTA ── */}
      <CtaSection>
        <CtaBg />
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <CtaBadge>Free Download</CtaBadge>
          <CtaTitle>Start Getting These Signals — For Free</CtaTitle>
          <CtaSub>
            Join thousands of traders already using Trend Genie's AI signals on Android.
            Download free. No subscription needed to start.
          </CtaSub>

          <CtaStatsRow>
            <CtaStat><CtaStatNum>10,000+</CtaStatNum><CtaStatLabel>Active Traders</CtaStatLabel></CtaStat>
            <CtaStatDivider />
            <CtaStat><CtaStatNum>24/7</CtaStatNum><CtaStatLabel>AI Market Scanning</CtaStatLabel></CtaStat>
            <CtaStatDivider />
            <CtaStat><CtaStatNum>Free</CtaStatNum><CtaStatLabel>To Download</CtaStatLabel></CtaStat>
          </CtaStatsRow>

          <CtaBtnRow>
            <CtaDownloadBtn
              href="https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie"
              target="_blank" rel="noopener noreferrer"
            >
              Download Free on Google Play
            </CtaDownloadBtn>
            <CtaSecondaryBtn as={Link} to="/insights">Read Insights</CtaSecondaryBtn>
          </CtaBtnRow>
          <CtaDisclaimer>
            * Portfolio growth assumes $100 per trade with 10x leverage. Past performance does not guarantee future results. Trading crypto carries significant risk.
          </CtaDisclaimer>
        </motion.div>
      </CtaSection>
    </PageWrapper>
  );
};

export default ResultsPage;

/* ============================================================
   KEYFRAMES
============================================================ */
const pulseAnim = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(0.75); }
`;

const loadingBounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
  40% { transform: translateY(-8px); opacity: 1; }
`;

const floatBg = keyframes`
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-20px) scale(1.04); }
`;

/* ============================================================
   PAGE WRAPPER
============================================================ */
const PageWrapper = styled.div`
  background: var(--bg-main);
  min-height: 100vh;
  padding-top: 4.5rem;
  font-family: 'Poppins', system-ui, sans-serif;
`;

/* ============================================================
   HERO
============================================================ */
const HeroSection = styled.section`
  position: relative;
  padding: 5rem 10% 4rem;
  text-align: center;
  overflow: hidden;
  background: linear-gradient(160deg, #f0f9ff 0%, #ffffff 55%, #f0fff8 100%);

  @media (max-width: 768px) {
    padding: 4rem 1.5rem 3rem;
  }
`;

const HeroBg = styled.div`
  position: absolute;
  width: 700px;
  height: 700px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(41,171,226,0.08), transparent 70%);
  top: -200px; left: -100px;
  pointer-events: none;
  animation: ${floatBg} 10s ease-in-out infinite;
`;

const HeroBg2 = styled.div`
  position: absolute;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(16,185,129,0.07), transparent 70%);
  bottom: -100px; right: -50px;
  pointer-events: none;
  animation: ${floatBg} 14s ease-in-out infinite reverse;
`;

const LiveBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  background: rgba(16,185,129,0.1);
  border: 1px solid rgba(16,185,129,0.3);
  color: #10b981;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  padding: 0.38rem 1rem;
  border-radius: 999px;
  margin-bottom: 1.4rem;
`;

const LiveDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #10b981;
  flex-shrink: 0;
  animation: ${pulseAnim} 1.4s ease-in-out infinite;
`;

const HeroTitle = styled.h1`
  font-size: 3.8rem;
  font-weight: 800;
  color: var(--text-color);
  line-height: 1.1;
  letter-spacing: -1.5px;
  margin-bottom: 1.2rem;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 2.6rem;
  }
`;

const GradientSpan = styled.span`
  background: linear-gradient(135deg, #10b981, #29ABE2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeroSub = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary-color);
  max-width: 600px;
  margin: 0 auto 2.2rem;
  line-height: 1.8;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const HeroCTARow = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  position: relative;
  z-index: 1;
`;

const DownloadBtn = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 1rem 2.2rem;
  border-radius: 14px;
  background: linear-gradient(135deg, #29ABE2, #1A5276);
  color: white;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  box-shadow: var(--shadow-primary);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-primary-lg);
  }
`;

const SecondaryBtn = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 1rem 1.8rem;
  border-radius: 14px;
  background: var(--bg-surface);
  color: var(--text-secondary-color);
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  border: 1.5px solid var(--border-color);
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
    transform: translateY(-2px);
  }
`;

const HeroNote = styled.p`
  font-size: 0.78rem;
  color: var(--text-muted);
  position: relative;
  z-index: 1;
`;

/* ============================================================
   LOADING
============================================================ */
const LoadingSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 5rem 0;
  flex-direction: column;
`;

const LoadingDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--primary-color);
  animation: ${loadingBounce} 1.2s ease-in-out infinite;
  display: inline-block;
`;

const LoadingText = styled.p`
  color: var(--text-muted);
  font-size: 0.9rem;
  margin-top: 0.8rem;
`;

/* ============================================================
   HERO KPI STRIP
============================================================ */
const HeroKpiStrip = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: center;
  flex-wrap: wrap;
  background: var(--bg-surface);
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
  padding: 1.8rem 10%;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const HeroKpi = styled.div`
  flex: 1;
  min-width: 140px;
  text-align: center;
  padding: 0.5rem 1rem;
`;

const HeroKpiVal = styled.div`
  font-size: 2.2rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${p => p.green ? '#10b981' : p.blue ? 'var(--primary-color)' : 'var(--text-color)'};
  line-height: 1.1;
  margin-bottom: 0.3rem;

  @media (max-width: 768px) {
    font-size: 1.7rem;
  }
`;

const HeroKpiLabel = styled.div`
  font-size: 0.82rem;
  color: var(--text-muted);
  font-weight: 500;
`;

const HeroKpiDivider = styled.div`
  width: 1px;
  background: var(--border-color);
  align-self: stretch;
  margin: 0.5rem 0;

  @media (max-width: 600px) {
    display: none;
  }
`;

/* ============================================================
   CONTENT SECTIONS
============================================================ */
const ContentSection = styled.section`
  padding: 5rem 10%;
  background: var(--bg-secondary);
  text-align: center;

  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
  }
`;

const BreakdownSection = styled.section`
  padding: 5rem 10%;
  background: var(--bg-main);
  text-align: center;

  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
  }
`;

const HowItWorksSection = styled.section`
  padding: 5rem 10%;
  background: var(--bg-secondary);
  text-align: center;

  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
  }
`;

const ComparisonSection = styled.section`
  padding: 5rem 10%;
  background: var(--bg-main);
  text-align: center;

  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
  }
`;

const SectionTag = styled.div`
  display: inline-block;
  background: var(--primary-light);
  color: var(--primary-color);
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 0.38rem 1rem;
  border-radius: 999px;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.4rem;
  font-weight: 800;
  color: var(--text-color);
  line-height: 1.2;
  letter-spacing: -0.5px;
  margin-bottom: 0.8rem;

  @media (max-width: 768px) {
    font-size: 1.9rem;
  }
`;

const SectionSub = styled.p`
  font-size: 1rem;
  color: var(--text-secondary-color);
  max-width: 580px;
  margin: 0 auto 3rem;
  line-height: 1.75;
`;

/* ============================================================
   KPI CARDS GRID
============================================================ */
const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.2rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 440px) {
    grid-template-columns: 1fr;
  }
`;

const KpiCardWrap = styled(motion.div)`
  position: relative;
  background: var(--bg-surface);
  border: 1.5px solid ${p => p.highlight ? 'rgba(16,185,129,0.3)' : 'var(--border-color)'};
  border-radius: 20px;
  padding: 1.8rem 1.4rem;
  text-align: center;
  box-shadow: ${p => p.highlight ? '0 4px 24px rgba(16,185,129,0.1)' : 'var(--shadow-sm)'};
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--shadow-lg);
  }
`;

const KpiCardAccent = styled.div`
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: linear-gradient(90deg, #10b981, #29ABE2);
  border-radius: 20px 20px 0 0;
`;

const KpiIcon = styled.div`
  font-size: 1.6rem;
  margin-bottom: 0.6rem;
`;

const KpiValue = styled.div`
  font-size: 2.4rem;
  font-weight: 800;
  color: ${p => p.color || 'var(--text-color)'};
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
  margin-bottom: 0.4rem;

  @media (max-width: 768px) {
    font-size: 1.9rem;
  }
`;

const KpiLabel = styled.div`
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 0.2rem;
`;

const KpiSublabel = styled.div`
  font-size: 0.72rem;
  color: var(--text-muted);
  line-height: 1.4;
`;

/* ============================================================
   WIN/LOSS BREAKDOWN
============================================================ */
const BreakdownCard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 24px;
  padding: 2.5rem;
  max-width: 800px;
  margin: 0 auto;
  box-shadow: var(--shadow-md);

  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const BreakdownRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const BreakdownSide = styled.div`
  flex: 1;
  text-align: center;
  padding: 1.5rem;
  background: ${p => p.type === 'win' ? 'rgba(16,185,129,0.06)' : 'rgba(239,68,68,0.05)'};
  border: 1px solid ${p => p.type === 'win' ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.15)'};
  border-radius: 16px;
`;

const BreakdownIcon = styled.div`
  font-size: 2rem;
  font-weight: 900;
  color: ${p => p.type === 'win' ? '#10b981' : '#ef4444'};
  line-height: 1;
  margin-bottom: 0.4rem;
`;

const BreakdownBigNum = styled.div`
  font-size: 3rem;
  font-weight: 800;
  color: var(--text-color);
  font-variant-numeric: tabular-nums;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const BreakdownLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-secondary-color);
  margin-bottom: 0.5rem;
`;

const BreakdownSubs = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const BreakdownSub = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);

  strong {
    color: var(--text-color);
  }
`;

const BreakdownCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WinRateCircle = styled.div`
  width: 110px;
  height: 110px;
  border-radius: 50%;
  background: conic-gradient(#10b981 0%, #10b981 ${p => p.pct || 70}%, #fee2e2 ${p => p.pct || 70}%, #fee2e2 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 8px white, 0 0 0 9px var(--border-color);
  background: linear-gradient(135deg, #f0fdf4, #dcfce7);
  border: 3px solid rgba(16,185,129,0.3);
`;

const WinRateCircleVal = styled.div`
  font-size: 1.6rem;
  font-weight: 800;
  color: #10b981;
  line-height: 1;
`;

const WinRateCircleLabel = styled.div`
  font-size: 0.62rem;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const BarSection = styled.div``;

const BarLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.78rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const StackedBarTrack = styled.div`
  width: 100%;
  height: 12px;
  background: #fee2e2;
  border-radius: 999px;
  overflow: hidden;
  display: flex;
`;

/* ============================================================
   HOW IT WORKS
============================================================ */
const StepsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.2rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const StepCard = styled(motion.div)`
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 2rem 1.5rem;
  text-align: center;
  box-shadow: var(--shadow-sm);
  transition: transform 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-md);
  }
`;

const StepNum = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #29ABE2, #1A5276);
  color: white;
  font-size: 1.2rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  box-shadow: var(--shadow-primary);
`;

const StepTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-color);
  margin-bottom: 0.6rem;
`;

const StepDesc = styled.p`
  font-size: 0.88rem;
  color: var(--text-secondary-color);
  line-height: 1.65;
`;

/* ============================================================
   COMPARISON
============================================================ */
const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const CompItem = styled(motion.div)`
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 18px;
  padding: 1.8rem;
  text-align: left;
  box-shadow: var(--shadow-sm);
`;

const CompLabel = styled.h4`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-color);
  margin-bottom: 1rem;
`;

const CompBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.5rem;
`;

const CompBarTag = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  color: ${p => p.ai ? 'var(--primary-color)' : 'var(--text-muted)'};
  width: 44px;
  flex-shrink: 0;
  text-align: right;
`;

const CompBarTrack = styled.div`
  flex: 1;
  height: 8px;
  background: var(--bg-tertiary);
  border-radius: 999px;
  overflow: hidden;
`;

const CompBarPct = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  color: ${p => p.ai ? 'var(--primary-color)' : 'var(--text-muted)'};
  width: 32px;
  flex-shrink: 0;
`;

const CompNote = styled.div`
  font-size: 0.72rem;
  color: ${p => p.ai ? 'var(--primary-color)' : 'var(--text-muted)'};
  margin-top: 0.2rem;
  font-weight: ${p => p.ai ? 600 : 400};
`;

/* ============================================================
   CTA SECTION
============================================================ */
const CtaSection = styled.section`
  position: relative;
  padding: 6rem 10%;
  text-align: center;
  background: linear-gradient(160deg, #f0f9ff 0%, #ffffff 50%, #f0fff8 100%);
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
  }
`;

const CtaBg = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse at 20% 50%, rgba(41,171,226,0.07), transparent 60%),
    radial-gradient(ellipse at 80% 50%, rgba(16,185,129,0.06), transparent 60%);
  pointer-events: none;
`;

const CtaBadge = styled.div`
  display: inline-block;
  background: var(--primary-light);
  color: var(--primary-color);
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 0.38rem 1rem;
  border-radius: 999px;
  margin-bottom: 1.2rem;
  position: relative;
  z-index: 1;
`;

const CtaTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 800;
  color: var(--text-color);
  line-height: 1.15;
  letter-spacing: -0.5px;
  margin-bottom: 1rem;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CtaSub = styled.p`
  font-size: 1rem;
  color: var(--text-secondary-color);
  max-width: 520px;
  margin: 0 auto 2.5rem;
  line-height: 1.75;
  position: relative;
  z-index: 1;
`;

const CtaStatsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 18px;
  padding: 1.5rem 2rem;
  max-width: 560px;
  margin: 0 auto 2.5rem;
  flex-wrap: wrap;
  box-shadow: var(--shadow-md);
  position: relative;
  z-index: 1;

  @media (max-width: 600px) {
    padding: 1.2rem 1rem;
  }
`;

const CtaStat = styled.div`
  flex: 1;
  text-align: center;
  padding: 0.3rem 0.5rem;
  min-width: 100px;
`;

const CtaStatNum = styled.div`
  font-size: 1.8rem;
  font-weight: 800;
  background: linear-gradient(135deg, #29ABE2, #1A5276);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.1;
`;

const CtaStatLabel = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.2rem;
`;

const CtaStatDivider = styled.div`
  width: 1px;
  height: 40px;
  background: var(--border-color);
  flex-shrink: 0;

  @media (max-width: 600px) {
    display: none;
  }
`;

const CtaBtnRow = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
  position: relative;
  z-index: 1;
`;

const CtaDownloadBtn = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 1.05rem 2.4rem;
  border-radius: 14px;
  background: linear-gradient(135deg, #29ABE2, #1A5276);
  color: white;
  font-weight: 700;
  font-size: 1.05rem;
  text-decoration: none;
  box-shadow: var(--shadow-primary);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-primary-lg);
  }
`;

const CtaSecondaryBtn = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 1.05rem 1.8rem;
  border-radius: 14px;
  background: var(--bg-surface);
  color: var(--text-secondary-color);
  font-weight: 600;
  font-size: 1.05rem;
  text-decoration: none;
  border: 1.5px solid var(--border-color);
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
    transform: translateY(-2px);
  }
`;

const CtaDisclaimer = styled.p`
  font-size: 0.72rem;
  color: var(--text-muted);
  max-width: 520px;
  margin: 0 auto;
  line-height: 1.6;
  position: relative;
  z-index: 1;
`;

/* ============================================================
   PENDING SIGNALS
============================================================ */
const PendingSection = styled.section`
  padding: 5rem 10%;
  background: var(--bg-secondary);
  text-align: center;

  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
  }
`;

const PendingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.4rem;
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const PendingCard = styled.div`
  background: var(--bg-surface);
  border: 1.5px solid var(--border-color);
  border-radius: 20px;
  padding: 1.5rem;
  text-align: left;
  box-shadow: var(--shadow-md);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);
  }
`;

const PendingCardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1.2rem;
`;

const PendingCoin = styled.div``;

const PendingSymbol = styled.div`
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--text-color);
  letter-spacing: -0.3px;
`;

const PendingExchange = styled.div`
  font-size: 0.72rem;
  color: var(--text-muted);
  margin-top: 0.1rem;
`;

const PendingPrices = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin-bottom: 1.2rem;
`;

const PendingPriceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.35rem 0.7rem;
  background: var(--bg-secondary);
  border-radius: 8px;
`;

const PendingPriceLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
`;

const PendingPriceVal = styled.span`
  font-size: 0.88rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${p => p.tp ? '#10b981' : p.sl ? '#ef4444' : p.entry ? 'var(--primary-color)' : 'var(--text-color)'};
`;

const PendingFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
  border-top: 1px solid var(--border-color);
  padding-top: 0.8rem;
`;

const PendingTime = styled.div`
  font-size: 0.7rem;
  color: var(--text-muted);
`;

const PendingStatusBadge = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  color: ${p => p.color};
  background: ${p => p.bg};
`;

/* ============================================================
   SIGNAL HISTORY TABLE
============================================================ */
const SignalHistorySection = styled.section`
  padding: 5rem 10%;
  background: var(--bg-main);
  text-align: center;

  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border-radius: 18px;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-md);
  background: var(--bg-surface);
`;

const SignalTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 0.85rem;
`;

const SignalTHead = styled.tr`
  background: var(--bg-secondary);

  th {
    padding: 1rem 1rem;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: var(--text-muted);
    border-bottom: 1px solid var(--border-color);
    white-space: nowrap;
  }
`;

const SignalTRow = styled.tr`
  border-bottom: 1px solid var(--border-color);
  transition: background 0.18s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--bg-secondary);
  }
`;

const SignalTd = styled.td`
  padding: 0.85rem 1rem;
  color: ${p => p.muted ? 'var(--text-muted)' : p.green ? '#10b981' : 'var(--text-color)'};
  font-variant-numeric: ${p => p.mono ? 'tabular-nums' : 'normal'};
  font-size: ${p => p.mono ? '0.82rem' : '0.85rem'};
  white-space: nowrap;
`;

const CoinCell = styled.div``;

const CoinSymbol = styled.div`
  font-weight: 800;
  color: var(--text-color);
  font-size: 0.92rem;
`;

const CoinExchange = styled.div`
  font-size: 0.68rem;
  color: var(--text-muted);
`;

const DirectionBadge = styled.span`
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  padding: 0.22rem 0.65rem;
  border-radius: 999px;
  background: ${p => p.short ? 'rgba(239,68,68,0.1)' : 'rgba(41,171,226,0.1)'};
  color: ${p => p.short ? '#ef4444' : '#29ABE2'};
  border: 1px solid ${p => p.short ? 'rgba(239,68,68,0.25)' : 'rgba(41,171,226,0.25)'};
`;

const GainBadge = styled.span`
  display: inline-block;
  font-size: 0.78rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  padding: 0.22rem 0.65rem;
  border-radius: 8px;
  background: ${p => p.win ? 'rgba(16,185,129,0.12)' : p.loss ? 'rgba(239,68,68,0.1)' : 'var(--bg-secondary)'};
  color: ${p => p.win ? '#10b981' : p.loss ? '#ef4444' : 'var(--text-muted)'};
`;

const StatusChip = styled.span`
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  color: ${p => p.color};
  background: ${p => p.bg};
  white-space: nowrap;
`;
